<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {ElButton, ElOption, ElSelect} from 'element-plus';
import {CabOrganization, DashboardPeriods, isUploadResultaat, UploadResultaat, uuidOf} from 'common';
import {dashboardPeriods, listObjects, UploadSoort} from '@/base/callBackend.js';
import {laadDashboard} from '@/base/dashboardState.js';
import {type UploadAntwoord, uploadMetVoortgang, type Voortgang} from '@/base/uploadMetVoortgang.js';
import UploadResultaatView from '@/views/admin/UploadResultaatView.vue';
import VoortgangView from '@/views/general/VoortgangView.vue';

const MISLUKT: UploadResultaat = {created: 0, replaced: 0, warnings: [], errors: ['upload mislukt']};

const organizations = ref<CabOrganization[]>([]);
const kwartalen     = ref<string[]>([]);
const pmoOrg        = ref<string | undefined>(undefined);
const pmoKwartaal   = ref<string>('');
const bezig         = ref<UploadSoort | undefined>(undefined);
const resultaten    = ref<Partial<Record<UploadSoort, UploadResultaat>>>({});
const voortgang     = ref<Partial<Record<UploadSoort, Voortgang>>>({});

onMounted(async () => {
    organizations.value                         = await listObjects(CabOrganization);
    const periods: DashboardPeriods | undefined = await laadDashboard(() => dashboardPeriods());
    kwartalen.value                             = periods ? [...periods.kwartalen].reverse() : [];
    pmoKwartaal.value                           = periods?.laatste ?? '';
});

// a hidden file input, like the database restore in TabBeheerAdmin
function kiesBestand(accept: string): Promise<File | undefined> {
    return new Promise(resolve => {
        const input: HTMLInputElement = document.createElement('input');
        input.type     = 'file';
        input.accept   = accept;
        input.onchange = () => resolve(input.files?.[0]);
        input.oncancel = () => resolve(undefined);
        input.click();
    });
}

async function upload(soort: UploadSoort) {
    const file: File | undefined = await kiesBestand(soort === 'zip' ? '.zip' : '.xlsx');
    if (!file) {
        return;
    }
    const body: FormData = new FormData();
    body.append('file', file);
    if (soort === 'pmo') {
        body.append('organization', pmoOrg.value ?? '');
        body.append('kwartaal', pmoKwartaal.value);
    }
    bezig.value = soort;
    const resultaat: UploadResultaat | undefined = await laadDashboard(async () => {
        const antwoord: UploadAntwoord = await uploadMetVoortgang(`/api/dashboard/upload/${soort}`, body,
            (v: Voortgang) => voortgang.value = {...voortgang.value, [soort]: v});
        return isUploadResultaat(antwoord.body) ? antwoord.body : {created: 0, replaced: 0, warnings: [], errors: [`upload mislukt (${antwoord.status})`]};
    });
    resultaten.value = {...resultaten.value, [soort]: resultaat ?? MISLUKT};
    voortgang.value  = {...voortgang.value, [soort]: undefined};
    bezig.value      = undefined;
}
</script>

<template>
    <div class="flex flex-col gap-6">
        <div class="text-2xl" data-testid="data-section-title">Upload</div>

        <section class="flex flex-col gap-2 rounded border border-gray-200 bg-white p-4">
            <div class="font-semibold text-zzc-blue">Kwartaalzip</div>
            <div class="text-sm text-gray-500">Een zip met een of meer kwartaalmappen (25Q2 of 2025-Q2) met daarin Codebook.xlsx, IZA-aanvraag voor BI-tool.xlsx en een map PMO met een bestand per organisatie. PMO-bestanden van elk kwartaal worden geimporteerd, Codebook en IZA alleen van het laatste kwartaal. Backups en andere bestanden worden genegeerd. De zzc-backup zips in de kwartaalmappen worden genegeerd, maar maken de upload groot en traag; laat ze bij voorkeur weg.</div>
            <div>
                <el-button type="primary" :disabled="bezig !== undefined" :loading="bezig === 'zip'" @click="upload('zip')">
                    Kies zip en upload
                </el-button>
            </div>
            <VoortgangView :voortgang="voortgang.zip"/>
            <UploadResultaatView :resultaat="resultaten.zip"/>
        </section>

        <section class="flex flex-col gap-2 rounded border border-gray-200 bg-white p-4">
            <div class="font-semibold text-zzc-blue">PMO initiatieven</div>
            <div class="text-sm text-gray-500">Het werkblad 'Overzicht initiatieven' van een PMO-bestand. Kies de organisatie en het kwartaal; de rijen van die combinatie worden vervangen.</div>
            <div class="flex flex-row items-center gap-2">
                <el-select v-model="pmoOrg" placeholder="organisatie" class="w-64" filterable>
                    <el-option v-for="o in organizations" :key="uuidOf(o)" :value="uuidOf(o)" :label="o.name"/>
                </el-select>
                <el-select v-model="pmoKwartaal" placeholder="kwartaal" class="w-32">
                    <el-option v-for="q in kwartalen" :key="q" :value="q" :label="q"/>
                </el-select>
                <el-button type="primary" :disabled="!pmoOrg || pmoKwartaal === '' || bezig !== undefined" :loading="bezig === 'pmo'" @click="upload('pmo')">
                    Kies bestand en upload
                </el-button>
            </div>
            <VoortgangView :voortgang="voortgang.pmo"/>
            <UploadResultaatView :resultaat="resultaten.pmo"/>
        </section>

        <section class="flex flex-col gap-2 rounded border border-gray-200 bg-white p-4">
            <div class="font-semibold text-zzc-blue">Codebook</div>
            <div class="text-sm text-gray-500">FTE targets, gemeten targets, maximale formatie targets, deeltijdfactoren en de target-regels van het transformatiedashboard. Aanwezige werkbladen vervangen de opgeslagen rijen.</div>
            <div>
                <el-button type="primary" :disabled="bezig !== undefined" :loading="bezig === 'codebook'" @click="upload('codebook')">
                    Kies bestand en upload
                </el-button>
            </div>
            <VoortgangView :voortgang="voortgang.codebook"/>
            <UploadResultaatView :resultaat="resultaten.codebook"/>
        </section>

        <section class="flex flex-col gap-2 rounded border border-gray-200 bg-white p-4">
            <div class="font-semibold text-zzc-blue">IZA financiele data</div>
            <div class="text-sm text-gray-500">Het werkblad 'Financiele data' van de IZA-aanvraag. Alle opgeslagen rijen worden vervangen.</div>
            <div>
                <el-button type="primary" :disabled="bezig !== undefined" :loading="bezig === 'financien'" @click="upload('financien')">
                    Kies bestand en upload
                </el-button>
            </div>
            <VoortgangView :voortgang="voortgang.financien"/>
            <UploadResultaatView :resultaat="resultaten.financien"/>
        </section>
    </div>
</template>
